import { WarningCircle } from "@phosphor-icons/react";
import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import type { ErrorStateProps } from "./ErrorState.types";

type ErrorStateVariant = "default" | "compact" | "full-page" | "table";

/* Exact compiled output of the pre-migration Tailwind utilities. */

const dangerPanel: CSSObject = {
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  borderColor: "hsl(var(--bfrs-color-danger) / .2)",
  backgroundColor: "hsl(var(--bfrs-color-danger-soft) / .6)"
};

const errorStateVariantStyles: Record<ErrorStateVariant, CSSObject> = {
  default: {
    minHeight: "14rem",
    ...dangerPanel,
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "2.5rem",
    paddingBottom: "2.5rem"
  },
  compact: {
    ...dangerPanel,
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "1.25rem",
    paddingBottom: "1.25rem"
  },
  "full-page": {
    minHeight: "320px",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "3rem",
    paddingBottom: "3rem"
  },
  table: {
    minHeight: "11rem",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "2rem",
    paddingBottom: "2rem"
  }
};

const ErrorStateRoot = styled("div", {
  label: "AgenticErrorState-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: ErrorStateVariant } }>(({ ownerState }) => [
  {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
  },
  errorStateVariantStyles[ownerState.variant]
]);

const ErrorStateIconBubble = styled("div", {
  label: "AgenticErrorState-iconBubble",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: ErrorStateVariant } }>(({ ownerState }) => [
  {
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  },
  ownerState.variant === "compact"
    ? { width: "2.5rem", height: "2.5rem" }
    : { width: "3rem", height: "3rem" }
]);

const ErrorStateTitle = styled("h3", {
  label: "AgenticErrorState-title",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: ErrorStateVariant } }>(({ ownerState }) => [
  {
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger-contrast) / var(--tw-text-opacity, 1))"
  },
  ownerState.variant === "compact"
    ? { fontSize: ".875rem", lineHeight: "1.25rem" }
    : { fontSize: "1rem", lineHeight: "1.5rem" }
]);

const ErrorStateDescription = styled("p", {
  label: "AgenticErrorState-description"
})({
  marginTop: ".5rem",
  maxWidth: "28rem",
  fontSize: ".875rem",
  lineHeight: "1.5rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const ErrorStateActions = styled("div", {
  label: "AgenticErrorState-actions"
})({
  marginTop: "1.25rem",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: ".5rem"
});

export const ErrorState = forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ className, icon, title = "Something went wrong", description, retryAction, secondaryAction, variant = "default", ...props }, ref) => (
    <ErrorStateRoot ref={ref} ownerState={{ variant }} className={className} role="alert" {...props}>
      <ErrorStateIconBubble ownerState={{ variant }}>
        {icon ?? <WarningCircle size={variant === "compact" ? 20 : 24} weight="duotone" />}
      </ErrorStateIconBubble>
      <ErrorStateTitle ownerState={{ variant }}>{title}</ErrorStateTitle>
      {description && <ErrorStateDescription>{description}</ErrorStateDescription>}
      {(retryAction || secondaryAction) && (
        <ErrorStateActions>
          {secondaryAction}
          {retryAction}
        </ErrorStateActions>
      )}
    </ErrorStateRoot>
  )
);

ErrorState.displayName = "ErrorState";
